import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useRef, useEffect, useLayoutEffect, Fragment } from "react";
import { Menu, X, Phone, Mail, Sun, Moon, ChevronDown } from "lucide-react";
import { useTheme } from "../lib/theme";
import { MEGA_MENU, VENDOR_LOGOS } from "../data/vendorLogos";
import ManagedTierEnquiryDialog from "./ManagedTierEnquiryDialog";
import CurrencySelector from "./CurrencySelector";
import ProductSearchAutocomplete from "./ProductSearchAutocomplete";
import { trackAdsQuoteRequest } from "../lib/analytics";

const megaKeys = ["microsoft", "msSubscription", "msDynamics", "adobe", "autodesk", "zoho", "tally", "quickheal", "seqrite", "chaos", "sketchup", "hardware", "cloud", "managed", "resources"];

// 25 Feb 2026 brief — three dedicated M365 landing pages injected in the
// main nav as short crawlable links, positioned immediately after the
// Microsoft mega trigger and before MS Subscription. Kept as short
// labels ("M365 Business" etc.) so the desktop navbar doesn't overflow.
const M365_QUICK_LINKS = [
  { to: "/microsoft-365-business",   label: "M365 Business" },
  { to: "/microsoft-365-enterprise", label: "M365 Enterprise" },
  { to: "/microsoft-365-frontline",  label: "M365 Frontline" },
];
// Additional vendor pages that appear on the mobile drawer as simple flat links.
// These are single-catalog vendors (no mega dropdown needed) but must still be
// discoverable from mobile navigation alongside the other product surfaces.
// SketchUp graduated to the mega menu on desktop; the mega menu itself already
// renders on mobile via `MEGA_MENU`, so listing SketchUp here would duplicate.
const mobileVendorFlat = [
  { to: "/corel", label: "Corel", logo: "https://img.icons8.com/color/96/coreldraw.png" },
];
const flatNav = [
  { to: "/products", label: "All Categories" },
  { to: "/estimate", label: "Get Estimate" },
  { to: "/services", label: "Services" },
  { to: "/software-web-development", label: "Web & Software Dev" },
  { to: "/contact", label: "Contact" },
];
const mobileOnlyNav = [
  { to: "/partners", label: "Partners" },
  { to: "/about", label: "About" },
];

function MegaPanel({ config, triggerRef, navRef, onClose, onMouseEnter, onTierEnquiry }) {
  const isTiered = config.variant === "tiered";
  const panelRef = useRef(null);
  // Panel starts invisible so it can be measured + positioned before paint.
  // `useLayoutEffect` reads the trigger's + nav's + viewport's rects and
  // clamps the panel horizontally so it can NEVER overflow the right edge
  // (or left edge, for that matter). Runs on config change AND resize.
  const [pos, setPos] = useState({ left: 0, ready: false });

  useLayoutEffect(() => {
    function recompute() {
      const trig = triggerRef?.current;
      const nav = navRef?.current;
      const panel = panelRef.current;
      if (!trig || !nav || !panel) return;
      const tRect = trig.getBoundingClientRect();
      const nRect = nav.getBoundingClientRect();
      const panelWidth = panel.offsetWidth;
      const vw = window.innerWidth;

      // Trigger's centre-X in viewport coordinates, then convert to
      // coordinates local to <nav> (which is our positioning parent).
      const triggerCentreVp = tRect.left + tRect.width / 2;
      const idealLeftLocal = triggerCentreVp - nRect.left - panelWidth / 2;

      // Keep panel fully inside viewport with a 16-px safety margin.
      // Bounds are converted from viewport → nav-local by subtracting
      // `nRect.left` so the CSS `left` value is directly usable.
      const minLeftLocal = 16 - nRect.left;
      const maxLeftLocal = vw - panelWidth - 16 - nRect.left;
      const clamped = Math.max(minLeftLocal, Math.min(idealLeftLocal, maxLeftLocal));

      setPos({ left: clamped, ready: true });
    }
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, { passive: true });
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute);
    };
  }, [config, triggerRef, navRef]);

  return (
    <div
      ref={panelRef}
      style={{ left: pos.left, opacity: pos.ready ? 1 : 0 }}
      // Positioned relative to <nav> — `top-full` = nav's bottom edge, so
      // even when the nav wraps into two rows the panel drops below BOTH.
      // Fixes GSC / user-reported "dropdown overlaps second navbar row".
      // Horizontal `left` is JS-computed above so panels near the right
      // edge (Managed Services / Resources) never overflow the viewport.
      className={`absolute top-full pt-2 ${isTiered ? "w-[1180px]" : "w-[880px]"} max-w-[calc(100vw-2rem)] z-50 transition-opacity`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onClose}
      data-testid={`mega-panel-${config.label.toLowerCase()}`}
    >
      <div className="rounded-md border border-border bg-popover shadow-2xl overflow-hidden">
      {isTiered ? (
        <TieredMegaPanel config={config} onClose={onClose} onTierEnquiry={onTierEnquiry} />
      ) : (
        <>
          {/* Grid auto-scales to the number of columns (up to 4). Every
              mega-menu ships with 3 columns by default; the MS
              Subscription menu (30 Aug 2026) uses 4 columns so Business
              / Enterprise / Frontline / Cloud Services each get their
              own vertical stack instead of wrapping onto a second row. */}
          <div className={`grid ${config.columns.length >= 4 ? "grid-cols-4" : "grid-cols-3"} gap-0`}>
            {config.columns.map((col) => (
              <div key={col.title} className="p-5 border-r border-border last:border-r-0">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">{col.title}</div>
                <ul className="space-y-1.5">
                  {col.items.map((it) => (
                    <li key={it.label}>
                      <Link
                        to={it.href}
                        onClick={onClose}
                        className="flex items-center gap-2.5 py-1.5 px-2 -mx-2 rounded-sm hover:bg-brand-blue/8 transition-colors group"
                      >
                        <img
                          src={it.logo}
                          alt=""
                          width="20"
                          height="20"
                          decoding="async"
                          className="w-5 h-5 object-contain shrink-0"
                          loading="lazy"
                          onError={(e) => { e.target.style.opacity = 0; }}
                        />
                        <span className="text-sm text-foreground/85 group-hover:text-brand-blue truncate">{it.label}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {config.footer && (
            <Link
              to={config.footer.href}
              onClick={onClose}
              className="block border-t border-border bg-muted/40 px-5 py-3 text-sm font-semibold text-brand-blue hover:bg-brand-blue hover:text-white transition-colors"
            >
              {config.footer.label}
            </Link>
          )}
        </>
      )}
      </div>
    </div>
  );
}

// Tiered variant — Silver / Gold / Diamond support + subcategories row.
function TieredMegaPanel({ config, onClose, onTierEnquiry }) {
  return (
    <>
      {/* Row 1 — intro + 3 tier cards */}
      <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_1fr_1fr] gap-3 p-4 bg-muted/30">
        <div className="flex flex-col justify-center pr-2">
          <img src={config.intro.icon} alt="" width="36" height="36" decoding="async" className="w-9 h-9 object-contain mb-2" loading="lazy" />
          <div className="font-display font-bold text-base text-brand-navy leading-tight">{config.intro.title}</div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{config.intro.desc}</div>
        </div>
        {config.tiers.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => { onTierEnquiry?.(t.key); onClose?.(); }}
            data-testid={`mega-tier-${t.key}`}
            className={`group text-left flex flex-col rounded-md border bg-gradient-to-br ${t.accent} p-3 hover:shadow-md hover:border-brand-blue transition-all cursor-pointer`}
          >
            <div className="flex items-start gap-2.5">
              <img src={t.icon} alt="" width="32" height="32" decoding="async" className="w-8 h-8 object-contain shrink-0" loading="lazy" onError={(e) => { e.target.style.opacity = 0; }} />
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-sm text-brand-navy leading-tight">{t.name}</div>
                <div className="text-[11px] text-slate-700/80 leading-snug mt-0.5">{t.subtitle}</div>
              </div>
            </div>
            <ul className="mt-2 grid grid-cols-2 gap-x-2 gap-y-0.5">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-1 text-[10.5px] text-slate-700/90 leading-snug">
                  <span className="text-emerald-600 mt-[1px] shrink-0">✓</span>
                  <span className="truncate">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-brand-blue font-bold opacity-70 group-hover:opacity-100 transition-opacity">
              Enquire now →
            </div>
          </button>
        ))}
      </div>

      {/* Row 2 — subcategories */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-t border-border">
        {config.subcategories.map((s) => (
          <div key={s.title} className="p-4 border-r border-border last:border-r-0 border-b lg:border-b-0">
            <Link
              to={s.href}
              onClick={onClose}
              data-testid={`mega-sub-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="flex items-start gap-2.5 group"
            >
              <img src={s.icon} alt="" width="32" height="32" decoding="async" className="w-8 h-8 object-contain shrink-0" loading="lazy" onError={(e) => { e.target.style.opacity = 0; }} />
              <div className="min-w-0">
                <div className="font-display font-bold text-sm text-brand-navy group-hover:text-brand-blue transition-colors leading-tight">{s.title}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">{s.subtitle}</div>
              </div>
            </Link>
            <ul className="mt-2.5 space-y-1">
              {s.items.map((it) => (
                <li key={it}>
                  <Link
                    to={s.href}
                    onClick={onClose}
                    className="flex items-start gap-1.5 text-[11.5px] text-foreground/80 hover:text-brand-blue leading-snug"
                  >
                    <span className="text-emerald-600 mt-[1px] shrink-0">✓</span>
                    <span>{it}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {config.footer && (
        <Link
          to={config.footer.href}
          onClick={onClose}
          className="block border-t border-border bg-muted/40 px-5 py-3 text-sm font-semibold text-brand-blue hover:bg-brand-blue hover:text-white transition-colors"
        >
          {config.footer.label}
        </Link>
      )}
    </>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openMega, setOpenMega] = useState(null);   // key of open dropdown
  const [mobileGroup, setMobileGroup] = useState(null);
  const [tierEnquiry, setTierEnquiry] = useState({ open: false, tier: "silver" });
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";
  const location = useLocation();
  const closeTimer = useRef(null);
  // Refs used by MegaPanel to compute overflow-safe horizontal positioning.
  // `navRef` is the positioning parent (position:relative). `triggerRefs`
  // holds one ref per mega trigger keyed by MEGA_MENU key, so the panel
  // can measure `triggerRef.getBoundingClientRect()` and clamp its `left`.
  const navRef = useRef(null);
  const triggerRefs = useRef({});
  // Hover-intent timer — prevents accidental panel switches when the mouse
  // briefly crosses another nav item while travelling toward the CURRENTLY
  // open mega panel. Fixed for two related UX bugs:
  //   (1) Dropdown closes too fast when moving mouse from trigger → panel.
  //   (2) On narrow viewports the navbar wraps into 2 rows; the mega panel
  //       renders anchored to the nav's bottom edge, so the mouse path from
  //       any row-1 trigger down to the panel briefly overlaps row-2 items
  //       (Managed Services, Resources, …), whose `onMouseEnter` would
  //       instantly swap the active dropdown.
  const openIntentTimer = useRef(null);
  // Small enough to feel snappy on intentional hovers, long enough to
  // absorb a brief flyover as the mouse aims at the current dropdown.
  const HOVER_OPEN_DELAY_MS = 120;
  // At least 150 ms — the user's ask. 220 ms gives comfortable travel
  // time to the panel even for slow / trackpad users.
  const HOVER_CLOSE_DELAY_MS = 220;

  const openWith = (k) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    if (openIntentTimer.current) clearTimeout(openIntentTimer.current);
    // If nothing is open yet — open immediately. Otherwise wait for the
    // mouse to dwell on the new trigger, so a flyover doesn't steal focus
    // from the panel the user is already navigating toward.
    if (openMega === null || openMega === k) {
      setOpenMega(k);
    } else {
      openIntentTimer.current = setTimeout(
        () => setOpenMega(k),
        HOVER_OPEN_DELAY_MS,
      );
    }
  };
  const cancelOpenIntent = () => {
    if (openIntentTimer.current) {
      clearTimeout(openIntentTimer.current);
      openIntentTimer.current = null;
    }
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    cancelOpenIntent();
    closeTimer.current = setTimeout(
      () => setOpenMega(null),
      HOVER_CLOSE_DELAY_MS,
    );
  };

  useEffect(() => {
    setOpenMega(null);
    setMobileOpen(false);
    setMobileGroup(null);
  }, [location.pathname, location.search]);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 border-b border-border/60 bg-background/95 backdrop-blur-xl">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Row 1 — logo + right actions */}
        <div className="flex h-16 md:h-20 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
            <img src="/logo.png" alt="India Enterprises" width="200" height="44" fetchpriority="high" decoding="async" className="h-9 md:h-11 w-auto" />
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-display font-black text-[15px] tracking-tight text-brand-navy whitespace-nowrap">INDIA ENTERPRISES</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground whitespace-nowrap">IT & Cloud Solutions Consultancy</span>
            </div>
          </Link>

          {/* Middle — global product search (hidden on small screens where the
              hamburger drawer already surfaces search).  Uses the same
              ProductSearchAutocomplete component that powers the hero
              search on the homepage so results / keyboard nav are
              identical everywhere. */}
          <div className="hidden md:block flex-1 max-w-md">
            <ProductSearchAutocomplete
              theme="light"
              placeholder="Search products…"
              testIdPrefix="header-search"
            />
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Email link — hidden on small screens to free tap space; still
                available in the mobile hamburger drawer + FloatingActions. */}
            <a
              href="mailto:sales@indiaenterprises.co"
              data-testid="header-email-btn"
              aria-label="Email sales@indiaenterprises.co"
              className="hidden md:inline-flex items-center gap-2 text-sm font-mono text-brand-navy hover:text-brand-blue transition-colors whitespace-nowrap"
            >
              <Mail className="h-4 w-4" />
              <span className="hidden lg:inline">sales@indiaenterprises.co</span>
            </a>
            {/* Call button — enlarged tap target (40×40) on mobile so it
                meets the 44-ish px iOS/Android recommendation while still
                looking icon-only. Desktop keeps the full phone number. */}
            <a
              href="tel:+918527694100"
              data-testid="header-call-btn"
              aria-label="Call +91 85276 94100"
              className="inline-flex h-10 w-10 md:h-auto md:w-auto items-center justify-center md:justify-start md:gap-2 rounded-sm md:rounded-none border md:border-0 border-border/70 md:px-0 text-sm font-mono text-brand-navy hover:text-brand-blue hover:border-brand-blue/60 md:hover:border-transparent transition-colors whitespace-nowrap"
            >
              <Phone className="h-4 w-4" />
              <span className="hidden md:inline">+91 85276 94100</span>
            </a>
            <CurrencySelector testId="header-currency-selector" />
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
              className="hidden sm:inline-flex h-10 w-10 items-center justify-center rounded-sm border border-border/70 text-brand-navy hover:text-brand-blue hover:border-brand-blue/60 transition-colors"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
            <Link
              to="/contact"
              data-testid="header-quote-btn"
              onClick={() => trackAdsQuoteRequest({ source: "header-desktop" })}
              className="hidden md:inline-flex items-center rounded-sm bg-brand-blue px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-blueDark transition-colors"
            >
              Request a Quote
            </Link>
            <button
              className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-sm text-brand-navy hover:bg-brand-blue/8 transition-colors -mr-1"
              onClick={() => setMobileOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile-only global product search row — sits directly under the
            top bar so touch users always have the app's #1 discovery tool
            one tap away, without the extra tap to open the hamburger. */}
        <div className="md:hidden pb-2 -mt-1">
          <ProductSearchAutocomplete
            theme="light"
            placeholder="Search products…"
            testIdPrefix="mobile-header-search"
          />
        </div>

        {/* Row 2 — full navigation (desktop only; mobile uses hamburger drawer).
            `min-h-11` instead of `h-11` so a narrow desktop viewport can wrap
            the nav into two rows without the panel overshooting into row-2
            hover zones (fixed as part of the hover-intent overhaul). */}
        <nav ref={navRef} className="hidden lg:flex relative items-stretch justify-center flex-wrap border-t border-border/40 min-h-11">
          <NavLink to="/" end data-testid="nav-home" className={({ isActive }) => `px-3 flex items-center text-[13px] font-medium transition-colors ${isActive ? "text-brand-blue" : "text-foreground/75 hover:text-brand-blue"}`}>Home</NavLink>

          {megaKeys.map((k) => {
            const cfg = MEGA_MENU[k];
            const isOpen = openMega === k;
            const isActive = location.pathname === cfg.href;
            // Grab-or-create the trigger's ref so MegaPanel can measure
            // its rect and clamp the panel's `left` inside the viewport.
            if (!triggerRefs.current[k]) triggerRefs.current[k] = { current: null };
            // 24 Feb 2026 brief — for MS Subscription + MS Dynamics only,
            // split the trigger into (a) a real crawlable <a href> covering
            // the label (navigates to the dedicated landing) and (b) a caret
            // button that toggles the dropdown. Hover-open still works via
            // the parent onMouseEnter. Other mega triggers stay as-is so
            // we don't touch unrelated navigation.
            const splitTrigger = k === "msSubscription" || k === "msDynamics";
            const node = splitTrigger ? (
              <div
                key={k}
                ref={(el) => { triggerRefs.current[k].current = el; }}
                className="flex"
                onMouseEnter={() => openWith(k)}
                onMouseLeave={() => { cancelOpenIntent(); scheduleClose(); }}
              >
                <Link
                  to={cfg.href}
                  data-testid={`nav-mega-${k}`}
                  onClick={() => setOpenMega(null)}
                  className={`pl-3 pr-1 flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap transition-colors ${
                    isActive ? "text-brand-blue" : "text-foreground/75 hover:text-brand-blue"
                  }`}
                >
                  <img src={cfg.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
                  {cfg.label}
                </Link>
                <button
                  type="button"
                  onClick={() => setOpenMega(isOpen ? null : k)}
                  data-testid={`nav-mega-${k}-caret`}
                  aria-label={`${cfg.label} menu`}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                  className={`pl-1 pr-3 flex items-center text-[13px] font-medium transition-colors ${
                    isActive ? "text-brand-blue" : "text-foreground/75 hover:text-brand-blue"
                  }`}
                >
                  <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
            ) : (
              <div
                key={k}
                ref={(el) => { triggerRefs.current[k].current = el; }}
                className="flex"
                onMouseEnter={() => openWith(k)}
                onMouseLeave={() => { cancelOpenIntent(); scheduleClose(); }}
              >
                <button
                  type="button"
                  onClick={() => setOpenMega(isOpen ? null : k)}
                  data-testid={`nav-mega-${k}`}
                  className={`px-3 flex items-center gap-1.5 text-[13px] font-medium whitespace-nowrap transition-colors ${
                    isActive ? "text-brand-blue" : "text-foreground/75 hover:text-brand-blue"
                  }`}
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                >
                  <img src={cfg.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" loading="lazy" onError={(e) => { e.target.style.display = "none"; }} />
                  {cfg.label}
                  <ChevronDown className={`h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
            );
            // 25 Feb 2026 — after the "microsoft" tile, splice in three
            // short crawlable NavLinks to the dedicated M365 landings.
            // Positioned here (inside the map) so they appear in DOM
            // order right after Microsoft ▼ and before MS Subscription ▼.
            if (k === "microsoft") {
              return (
                <Fragment key={k}>
                  {node}
                  {M365_QUICK_LINKS.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      data-testid={`nav-m365-${n.to.replace("/microsoft-365-", "")}`}
                      className={({ isActive }) => `px-2 flex items-center text-[13px] font-medium whitespace-nowrap transition-colors ${isActive ? "text-brand-blue" : "text-foreground/75 hover:text-brand-blue"}`}
                    >
                      {n.label}
                    </NavLink>
                  ))}
                </Fragment>
              );
            }
            return node;
          })}

          {/* Mega panel rendered ONCE at the nav level (not inside a trigger)
              so it can be positioned relative to <nav> — that way `top-full`
              anchors to the nav's bottom edge and the panel always drops
              below BOTH wrapped rows on narrow desktop viewports. Fixes
              the "dropdown overlaps second navbar row" bug reported by
              the user. Horizontal position is JS-clamped inside MegaPanel. */}
          {openMega && MEGA_MENU[openMega] && (
            <MegaPanel
              config={MEGA_MENU[openMega]}
              triggerRef={triggerRefs.current[openMega]}
              navRef={navRef}
              onClose={scheduleClose}
              onMouseEnter={() => openWith(openMega)}
              onTierEnquiry={(tier) => setTierEnquiry({ open: true, tier })}
            />
          )}

          {flatNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `px-2.5 flex items-center text-[13px] font-medium whitespace-nowrap transition-colors ${isActive ? "text-brand-blue" : "text-foreground/75 hover:text-brand-blue"}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-border py-3" data-testid="mobile-menu-panel">
            <div className="flex flex-col gap-1 pb-3">
              <NavLink to="/" end onClick={() => setMobileOpen(false)} className={({ isActive }) => `px-3 py-2 text-sm font-medium rounded-sm ${isActive ? "bg-brand-blue/10 text-brand-blue" : "text-foreground/80"}`}>Home</NavLink>

              {megaKeys.map((k) => {
                const cfg = MEGA_MENU[k];
                const open = mobileGroup === k;
                // 24 Feb 2026 brief — mobile MS Subscription + MS Dynamics
                // triggers split into label-Link + caret-button so the
                // dedicated landing pages are accessible directly from mobile.
                const splitTrigger = k === "msSubscription" || k === "msDynamics";
                // 25 Feb 2026 brief — after the "microsoft" tile, splice
                // in the 3 dedicated M365 landing links so they're visible
                // in the mobile drawer without needing to expand a group.
                const injectM365After = k === "microsoft";
                const groupNode = (
                  <div key={k}>
                    {splitTrigger ? (
                      <div className="w-full flex items-center rounded-sm text-foreground/80">
                        <Link
                          to={cfg.href}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mobile-nav-mega-${k}`}
                          className="flex-1 flex items-center gap-2 px-3 py-2 text-sm font-medium hover:text-brand-blue"
                        >
                          <img src={cfg.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" />
                          {cfg.label}
                        </Link>
                        <button
                          type="button"
                          onClick={() => setMobileGroup(open ? null : k)}
                          data-testid={`mobile-nav-mega-${k}-caret`}
                          aria-label={`${cfg.label} menu`}
                          aria-expanded={open}
                          className="px-3 py-2 text-sm text-foreground/70 hover:text-brand-blue"
                        >
                          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setMobileGroup(open ? null : k)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-sm text-foreground/80"
                      >
                        <span className="flex items-center gap-2">
                          <img src={cfg.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" />
                          {cfg.label}
                        </span>
                        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                      </button>
                    )}
                    {open && (
                      <div className="ml-3 pl-2 border-l border-border py-1">
                        {cfg.variant === "tiered" ? (
                          <>
                            <div className="px-3 pt-1 pb-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                              Managed IT Support · pick a tier
                            </div>
                            {cfg.tiers.map((t) => (
                              <button
                                key={t.key}
                                type="button"
                                onClick={() => {
                                  setTierEnquiry({ open: true, tier: t.key });
                                  setMobileOpen(false);
                                  setMobileGroup(null);
                                }}
                                data-testid={`mobile-mega-tier-${t.key}`}
                                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-foreground/85 hover:text-brand-blue"
                              >
                                <img src={t.icon} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.opacity = 0; }} />
                                <span className="font-semibold">{t.name}</span>
                                <span className="text-[10px] text-muted-foreground truncate">{t.subtitle}</span>
                              </button>
                            ))}
                            <div className="px-3 pt-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                              Solutions
                            </div>
                            {cfg.subcategories.map((s) => (
                              <Link
                                key={s.title}
                                to={s.href}
                                onClick={() => setMobileOpen(false)}
                                className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground/75 hover:text-brand-blue"
                              >
                                <img src={s.icon} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" onError={(e) => { e.target.style.opacity = 0; }} />
                                {s.title}
                              </Link>
                            ))}
                          </>
                        ) : (
                          (cfg.columns || []).flatMap((c) => c.items).slice(0, 6).map((it) => (
                            <Link
                              key={it.label + it.href}
                              to={it.href}
                              onClick={() => setMobileOpen(false)}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm text-foreground/75 hover:text-brand-blue"
                            >
                              <img src={it.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" />
                              {it.label}
                            </Link>
                          ))
                        )}
                        {cfg.footer && (
                          <Link
                            to={cfg.footer.href}
                            onClick={() => setMobileOpen(false)}
                            className="block px-3 py-1.5 text-xs font-semibold text-brand-blue"
                          >
                            {cfg.footer.label}
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                );
                if (injectM365After) {
                  return (
                    <Fragment key={k}>
                      {groupNode}
                      {M365_QUICK_LINKS.map((n) => (
                        <NavLink
                          key={n.to}
                          to={n.to}
                          onClick={() => setMobileOpen(false)}
                          data-testid={`mobile-nav-m365-${n.to.replace("/microsoft-365-", "")}`}
                          className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm ${isActive ? "bg-brand-blue/10 text-brand-blue" : "text-foreground/80"}`}
                        >
                          <img src={cfg.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" />
                          {n.label}
                        </NavLink>
                      ))}
                    </Fragment>
                  );
                }
                return groupNode;
              })}

              {[...mobileVendorFlat, ...flatNav, ...mobileOnlyNav].map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) => `flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-sm ${isActive ? "bg-brand-blue/10 text-brand-blue" : "text-foreground/80"}`}
                >
                  {n.logo && <img src={n.logo} alt="" width="16" height="16" decoding="async" className="w-4 h-4 object-contain" />}
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                onClick={() => { setMobileOpen(false); trackAdsQuoteRequest({ source: "header-mobile" }); }}
                data-testid="header-mobile-quote-btn"
                className="mt-2 inline-flex items-center justify-center rounded-sm bg-brand-blue px-4 py-2.5 text-sm font-semibold text-white"
              >
                Request a Quote
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Managed Services tier enquiry dialog — triggered from Managed Services mega panel */}
      <ManagedTierEnquiryDialog
        open={tierEnquiry.open}
        onOpenChange={(v) => setTierEnquiry((s) => ({ ...s, open: v }))}
        initialTier={tierEnquiry.tier}
      />
    </header>
  );
}
